import React from 'react';

import { type Breakpoints } from '@hivebrite/design-system/Theme';

import {
  useBuilderConfigContext,
  useDeviceModeContext,
} from 'hb-builder/hooks';
import ThemeProvider from 'hb-react/style/ThemeProvider';

export enum VIEW_AS {
  ADMIN = 'admin',
  USER = 'user',
}

export enum CONTAINER_TYPE {
  CARD = 'card',
  PAGE = 'page',
}

const fontBrightnessThemingKeys = {
  [CONTAINER_TYPE.CARD]: 'fo_frame_font_brightness',
  [CONTAINER_TYPE.PAGE]: 'fo_outboxed_font_brightness',
};

export const fakeBreakpointsForMobileMode: Array<string> & Breakpoints = [
  '100000px',
  '100000px',
  '100000px',
];

const [m, l, xl] = fakeBreakpointsForMobileMode;

fakeBreakpointsForMobileMode.m = m;
fakeBreakpointsForMobileMode.l = l;
fakeBreakpointsForMobileMode.xl = xl;

const BuilderThemeProvider = ({
  children,
  containerType = CONTAINER_TYPE.PAGE,
  viewAs,
}: {
  children: React.ReactNode;
  containerType?: CONTAINER_TYPE;
  viewAs: VIEW_AS;
}) => {
  const {
    hivebrite: {
      networkColors: customNetworkColors,
      networkTheming: customNetworkTheming,
    },
  } = useBuilderConfigContext();
  const { isMobileMode } = useDeviceModeContext();

  const fontBrightness =
    customNetworkTheming?.fo?.[fontBrightnessThemingKeys[containerType]];

  return (
    <ThemeProvider
      theme="fo"
      // This prop is used to disable the cache of styled-system.
      // It's needed as otherwise the cache is not invalidated when switching from desktop to mobile mode
      // and the breakpoints change so the components still have their desktop style (and vice versa).
      builderDisableStyledSystemCache
      fontBrightness={fontBrightness}
      {...(viewAs === VIEW_AS.USER && {
        options: {
          customNetworkColors,
          customNetworkTheming,
        },
      })}
      // @ts-expect-error untyped
      builderCustomBreakpoints={
        isMobileMode && viewAs === VIEW_AS.USER
          ? fakeBreakpointsForMobileMode
          : undefined
      }
    >
      {children}
    </ThemeProvider>
  );
};

export default BuilderThemeProvider;
